import {
	checkHasApprovalHierarchy,
	compareStrings,
	disabledByAnotherAction,
	getCurrency,
	highestTierSplitPartiallyApproved,
	isHierarchyNTEBased,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import * as Sentry from '@sentry/react';
import { Button, Col, message, Popover, Row } from 'antd';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { CURRENCIES, ROLE_TYPES } from '../../utils/DataConstants';
import {
	isAllowedToPerform,
	isSupplierAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import {
	checkSequentialApprovalBlock,
	compareApprovalTiers,
} from '../../utils/ApprovalHierarchyUtils';
import {
	forwardApprovalToUserForBuyer,
	forwardApprovalToUserForSupplier,
} from '../../thunks/company_approval_thunks';
import { infinityButNumber } from '../../constants/general';
import ApprovalHierarchyDisplayPurchaseRequestModal from './ApprovalHierarchyDisplayPurchaseRequestModal';
import { PURCHASE_REQUEST_STATUSES } from '../../constants/PurchaseRequestStatuses';
import {
	purchaseRequestStatusChangeForSupplier,
	syncPurchaseRequestApprovalHierarchyForSupplier,
} from '../../thunks/purchase_requests_thunks';
import { withUserType } from '../withUserType';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';

function ApprovalHierarchyDisplayPurchaseRequest({
	purchaseRequest,

	currentUser,
	approvePurchaseRequest,
	declinePurchaseRequest,
	fetchFieldTech,
	syncPurchaseRequest,

	refresh,
	style = {},
	allowedToApprove = true,

	companyConfig,
	userType,
}) {
	const defaultActionsAllowed = true;
	const [showModal, setShowModal] = useState(false);
	const [actionButtonLoading, setActionButtonLoading] = useState(null);
	const status = nullSafeGet('status', purchaseRequest);
	const reason = useRef();
	const setReason = (str) => (reason.current = str);
	const showStatus = (status) => {
		switch (status) {
			case 'pending':
				return 'Pending Approvers';
			case 'declined':
				return 'Approvers/Disputers';
			case 'awarded':
				return 'Approvers';
			case 'voided':
				return 'Approver List';
			default:
				return 'Approver List';
		}
	};
	const hasApprovalHierarchy = checkHasApprovalHierarchy(purchaseRequest);
	const sequentialApprovalEnforcement = nullSafeGetOrElse(
		'approvalHierarchy.sequentialApprovalEnforcement',
		purchaseRequest,
		false
	);
	const userTier = (tiers) =>
		hasApprovalHierarchy
			? tiers.filter((tier) =>
					tier.approvers.some((approver) => compareStrings(approver.email, currentUser.email))
			  ).length > 0
				? tiers.find((tier) =>
						tier.approvers.some((approver) => compareStrings(approver.email, currentUser.email))
				  )
				: undefined
			: tiers[0];

	const currency = getCurrency({ workOrder: nullSafeGet('workOrder', purchaseRequest) });
	const locationCurrency =
		CURRENCIES[nullSafeGetOrElse('workOrder.locationCurrencyId', purchaseRequest, 'USD')];
	const buyerCompanyCurrency = getCurrency({
		buyerCompany: nullSafeGet('workOrder.buyerCompany', purchaseRequest),
	});
	const exchangeRate =
		currency.id === buyerCompanyCurrency.id
			? 1
			: nullSafeGetOrElse('workOrder.currencyExchangeRateLocationToSupplier', purchaseRequest, 1) *
			  nullSafeGetOrElse('workOrder.currencyExchangeRateBuyerToLocation', purchaseRequest, 1);
	let purchaseRequestTotal = nullSafeGet('totalCost', purchaseRequest) / exchangeRate;
	let lessThanPurchaseRequest = true;
	let highestValidAmount = 0;
	let highestApprovedAmount = 0;
	const highestApprovalTierAmount = (tiers) => {
		let highestTiers = [...tiers]
			.filter((tier) => {
				let approvedByUsers = nullSafeGet('approvedByUsers', tier);
				let declinedByUsers = nullSafeGet('declinedByUsers', tier);
				if (approvedByUsers && declinedByUsers) {
					return approvedByUsers.concat(declinedByUsers).length > 0;
				} else {
					return false;
				}
			})
			.map((tier) => {
				return nullSafeGetOrElse('amountUpto', tier, infinityButNumber);
			});
		if (highestTiers.length > 0) {
			return highestTiers[highestTiers.length - 1];
		}
		return 0;
	};

	let lastAmount = 0;
	const isNTEBased = isHierarchyNTEBased(
		nullSafeGetOrElse('approvalHierarchy.tierDetails', purchaseRequest, [])
	);
	const tiers = nullSafeGet('approvalHierarchy.tierDetails', purchaseRequest)
		? nullSafeGet('approvalHierarchy.tierDetails', purchaseRequest)
				.sort(compareApprovalTiers)
				//Setup tier objects
				.map((tier) => {
					if (nullSafeGetOrElse('approvedByUsers', tier, []).length > 0) {
						highestApprovedAmount = parseInt(nullSafeGet('amountUpto', tier)) || Infinity;
					}
					return {
						...tier,
						approvedByUsers: nullSafeGetOrElse('approvedByUsers', tier, []).filter(
							(user, i, users) => i === users.findIndex((u) => compareStrings(u.email, user.email))
						),
						declinedByUsers: nullSafeGetOrElse('declinedByUsers', tier, []).filter(
							(user, i, users) => i === users.findIndex((u) => compareStrings(u.email, user.email))
						),
					};
				})
				.map((tier, i, tiers) => {
					let message;
					let isVisible = () => {
						if (!isNTEBased) {
							//We want to see all tiers when it is not NTE Based
							return true;
						}
						//We also want to display tiers which fit one of the following criteria:
						let amount = parseInt(nullSafeGet('amountUpto', tier));

						//1.This tier has either approvers or decliners and is >= the highest amount approved
						if (
							highestApprovedAmount > amount &&
							tier.approvedByUsers.length === 0 &&
							tier.declinedByUsers.length === 0
						) {
							//Not shown because a higher tier already approved
							message = 'A higher tier has already approved';
							return false;
						}
						if (
							highestApprovedAmount === amount &&
							tier.approvers.length === 0 &&
							tier.approvedByUsers.length === 0
						) {
							//Not shown because an equal tier has already approved and this tier has no approvers
							message =
								'An equal tier has approved and this tier has no approvers for this location';
							return false;
						}
						if (!lessThanPurchaseRequest) {
							let lastAmount = parseInt(nullSafeGetOrElse('amountUpto', tiers[i - 1], 0));
							//3. This tier has already been approved or declined
							if (tier.approvedByUsers.length > 0 || tier.declinedByUsers.length > 0) {
								return true;
							} else {
								//4. This tier is the same amount as the previous tier
								if (highestValidAmount === amount && highestValidAmount === lastAmount) {
									return true;
								} else {
									message = `Approval from this tier isn't required for purchase requests less than ${currency.format(
										(lastAmount ? lastAmount : amount) * exchangeRate
									)}`;
									return false;
								}
							}
						}

						if (amount && purchaseRequestTotal) {
							if (parseFloat(amount) > parseFloat(purchaseRequestTotal)) {
								//2. This tier is one tier over the purchaseRequest and contains potential approvers
								highestValidAmount = amount;
								if (nullSafeGetOrElse('approvers.length', tier, 0) > 0) {
									lessThanPurchaseRequest = false;
								}
							}
						} else {
							//1. This tier is less than the purchaseRequest
							lessThanPurchaseRequest = false;
						}
						return true;
					};
					lastAmount =
						nullSafeGetOrElse('amountUpto', tiers[i - 1], 0) === nullSafeGet('amountUpto', tier)
							? lastAmount
							: nullSafeGetOrElse('amountUpto', tiers[i - 1], 0);
					return { ...tier, isVisible: isVisible(), message, lastAmount };
				})
		: [];
	const hasApprovers = (tiers) =>
		tiers.some((tier) => nullSafeGetOrElse('approvers', tier, []).length > 0);
	const showApproval = [PURCHASE_REQUEST_STATUSES.requested].some((status) =>
		compareStrings(status, purchaseRequest.status)
	);
	const highestAmount = highestApprovalTierAmount(tiers);
	let myTier = userTier(tiers);
	const myTierAmount = nullSafeGet('amountUpto', myTier);

	// this condition is because of concurrency on workorder status , where a user updates something on the stale state.
	if (
		highestApprovedAmount > purchaseRequestTotal &&
		!highestTierSplitPartiallyApproved(tiers, highestApprovedAmount) &&
		showApproval
	) {
		// the highest approved amount is greater than the nte
		// and the work order is still pending. there is some bug. so we will reset the tiers so that nobody has approved.
		Sentry.captureException(
			`Error state for purchase request: Resetting Approval Hierarchy ${purchaseRequest.id}`
		);
		tiers.forEach((tier) => {
			tier.approvedByUsers = [];
			tier.declinedByUsers = [];
		});
	}

	const currentPurchaseRequest = {
		...purchaseRequest,
		approvalHierarchy: { ...purchaseRequest.approvalHierarchy, tierDetails: tiers },
		highestAmount: highestAmount,
	};
	const allowApproval = useMemo(() => {
		if (status !== PURCHASE_REQUEST_STATUSES.approved) {
			if (hasApprovalHierarchy && myTier !== undefined) {
				if (sequentialApprovalEnforcement) {
					const seqCheck = checkSequentialApprovalBlock(
						tiers,
						myTier,
						nullSafeGet('createdAt', purchaseRequest)
					);
					if (seqCheck.blocked) {
						setReason(seqCheck.reason);
						return false;
					}
				}
				if (myTierAmount === undefined || parseFloat(myTierAmount) >= highestAmount) {
					if (
						!nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
							compareStrings(currentUser.email, user.email)
						)
					) {
						return true;
					} else {
						setReason('Your tier has already approved.');
						return false;
					}
				}
			} else if (!hasApprovalHierarchy) {
				//No approval Hierarchy , default to supervisors
				return defaultActionsAllowed;
			}
			setReason('You are not in the approval hierarchy.');
		} else {
			setReason('This purchase request has already been approved.');
		}
		return false;
	}, [
		status,
		purchaseRequest,
		currentUser.email,
		hasApprovalHierarchy,
		myTier,
		myTierAmount,
		highestAmount,
		defaultActionsAllowed,
		sequentialApprovalEnforcement,
		tiers,
	]);
	const allowDecline = useMemo(() => {
		if (
			status !== PURCHASE_REQUEST_STATUSES.approved &&
			status !== PURCHASE_REQUEST_STATUSES.denied
		) {
			if (hasApprovalHierarchy && myTier) {
				if (sequentialApprovalEnforcement) {
					const seqCheck = checkSequentialApprovalBlock(
						tiers,
						myTier,
						nullSafeGet('createdAt', purchaseRequest)
					);
					if (seqCheck.blocked) {
						setReason(seqCheck.reason);
						return false;
					}
				}
				if (myTierAmount === undefined || parseFloat(myTierAmount) >= highestAmount) {
					if (
						!nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
							compareStrings(currentUser.email, user.email)
						)
					) {
						return true;
					} else {
						setReason('Your tier has already approved.');
						return false;
					}
				}
			} else if (!hasApprovalHierarchy && myTier !== undefined) {
				if (
					nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
						compareStrings(currentUser.email, user.email)
					)
				) {
					setReason('You have already approved.');
					return false;
				} else if (nullSafeGetOrElse('approvedByUsers', myTier, []).length > 1) {
					setReason('Someone has already approved.');
					return false;
				} else {
					setReason(`You are not allowed to approve/dispute purchase request.`);
					return defaultActionsAllowed;
				}
			} else if (!hasApprovalHierarchy && myTier === undefined) {
				setReason(`You are not allowed to approve/dispute purchase request.`);
				return defaultActionsAllowed;
			}
		}
		if (status === 'declined') {
			setReason('This purchase request has already been declined.');
		}
		return false;
	}, [
		status,
		hasApprovalHierarchy,
		myTier,
		myTierAmount,
		highestAmount,
		defaultActionsAllowed,
		sequentialApprovalEnforcement,
		tiers,
		purchaseRequest,
	]);

	const handleApprovePurchaseRequest = (purchaseRequest) => {
		approvePurchaseRequest(purchaseRequest.id).then((resultPurchaseRequest) => {
			setActionButtonLoading(null);
			message.destroy('purchaseRequestDetailMessageUpdating');
			message.success('Approved!');
			refresh();
		});
	};
	const handleDenyPurchaseRequest = (values) => {
		setActionButtonLoading(PURCHASE_REQUEST_STATUSES.denied);
		declinePurchaseRequest(purchaseRequest.id).then((resultPurchaseRequest) => {
			message.success(`Purchase request declined.`);
			setActionButtonLoading(null);
			refresh();
		});
	};

	const purchaseRequestString =
		purchaseRequest && purchaseRequest.status
			? compareStrings(purchaseRequest.status, PURCHASE_REQUEST_STATUSES.requested)
				? 'This purchase request is on hold until it has been fully approved'
				: compareStrings(purchaseRequest.status, PURCHASE_REQUEST_STATUSES.approved)
				? 'This purchase request has been approved'
				: compareStrings(purchaseRequest.status, PURCHASE_REQUEST_STATUSES.denied)
				? 'This purchase request has been declined'
				: 'This purchase request has been voided'
			: null;

	const purchaseRequestSubString =
		purchaseRequest && purchaseRequest.status
			? compareStrings(purchaseRequest.status, PURCHASE_REQUEST_STATUSES.requested)
				? 'Please review the details below and either approve the purchase request or deny it.'
				: null
			: null;

	return (
		<div className={'flex-col'}>
			<Row style={{ flexWrap: 'nowrap' }}>
				<Col style={{ flexGrow: 1 }}>
					<div className={'flex-row'}>
						<ApprovalHierarchyDisplayPurchaseRequestModal
							isNTEBased={isNTEBased}
							tiers={tiers}
							hasApprovalHierarchy={hasApprovalHierarchy}
							purchaseRequest={currentPurchaseRequest}
							setVisible={setShowModal}
							visible={showModal}
							style={style}
							exchangeRate={exchangeRate}
							currency={currency}
							currentUser={currentUser}
							syncPurchaseRequest={syncPurchaseRequest}
							refresh={refresh}
						/>
						<h5>{purchaseRequestString}</h5>
						<div style={{ fontSize: 16 }}>{purchaseRequestSubString}</div>
					</div>
					{allowedToApprove ? (
						<div
							style={{
								display: 'inline-flex',
								position: 'relative',
								marginTop: 16,
							}}
						>
							<div style={{ gap: '16px', alignContent: 'flex-start' }} className={'flex flex-wrap'}>
								{showApproval ? (
									allowApproval ? (
										<Button
											type="primary"
											key="approve"
											loading={actionButtonLoading === PURCHASE_REQUEST_STATUSES.approved}
											disabled={disabledByAnotherAction(
												actionButtonLoading,
												PURCHASE_REQUEST_STATUSES.approved
											)}
											onClick={() => {
												handleApprovePurchaseRequest(currentPurchaseRequest);
											}}
										>
											Approve Purchase Request
										</Button>
									) : (
										<Popover
											title={`Why can't I approve?`}
											content={nullSafeGet('current', reason)}
										>
											<Button type="primary" key="approve" disabled={true}>
												Approve Purchase Request
											</Button>
										</Popover>
									)
								) : null}
								{showApproval ? (
									allowDecline ? (
										<Button
											key="reject"
											loading={actionButtonLoading === PURCHASE_REQUEST_STATUSES.denied}
											disabled={disabledByAnotherAction(
												actionButtonLoading,
												PURCHASE_REQUEST_STATUSES.denied
											)}
											onClick={() => {
												handleDenyPurchaseRequest(currentPurchaseRequest);
											}}
										>
											Deny Purchase Request
										</Button>
									) : (
										<Popover
											title={`Why can't I decline?`}
											content={nullSafeGet('current', reason)}
										>
											<Button key="reject" disabled={true}>
												Deny Purchase Request
											</Button>
										</Popover>
									)
								) : null}
								{hasApprovalHierarchy ? (
									<div
										className={'inline-flex flex-row items-center'}
										style={{ cursor: 'pointer', fontSize: '16px' }}
										onClick={() => setShowModal(true)}
									>
										<i className="icons8-font icons8-multiple-users" />
										<span style={{ marginLeft: '8px', textDecoration: 'underline' }}>
											{showStatus(status)}
										</span>
									</div>
								) : null}
							</div>
						</div>
					) : null}
				</Col>
			</Row>
		</div>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	forwardApprovalToUser: (entity, email) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? forwardApprovalToUserForSupplier('proposal', entity, email)
				: forwardApprovalToUserForBuyer('proposal', entity, email)
		),
	approvePurchaseRequest: (id) => dispatch(purchaseRequestStatusChangeForSupplier(id, 'approved')),
	declinePurchaseRequest: (id) => dispatch(purchaseRequestStatusChangeForSupplier(id, 'denied')),
	syncPurchaseRequest: (entity) =>
		dispatch(syncPurchaseRequestApprovalHierarchyForSupplier(entity)),
	fetchFieldTech: (email) => dispatch(fieldTechsRestCrudThunksForSupplier.readOne(email)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(ApprovalHierarchyDisplayPurchaseRequest)
);
